import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertInfoComponent } from "../../../../shared/components/alert-info/alert-info.component";
import { DialogService } from '../../../services/dialog.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { ToastService } from '../../../services/toast.service';
import { FormattedUseraliasData, IResetPasswordResponse, TextField, Useralias } from '../../../models/model';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { ResetPasswordUserIdTableComponent } from "./reset-password-userId-table/reset-password-userId-table.component";
import { NgxCaptchaModule } from 'ngx-captcha';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { sanitizeInput } from '../../../../shared/utils/sanitize-input';
@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    TranslateModule,
    RouterLink,
    AlertInfoComponent,
    MatProgressSpinnerModule,
    ResetPasswordUserIdTableComponent,
    NgxCaptchaModule
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  fb = inject(FormBuilder);
  dialogService = inject(DialogService);
  authService = inject(AuthenticationService);
  toastService = inject(ToastService);
  translate = inject(TranslateService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  submitted = false;
  isLoading = false;
  useraliaslist: FormattedUseraliasData[] = [];
  userAliasDetails!:FormattedUseraliasData;
  isDisabled = false;
  siteKey = environment.captchaSiteKey;
  //siteKey = '6LfIAWUrAAAAAA-EWl7SskbKHBJxbWq-dF5JgxTf'
  captchaResolved = false;
  portalType = 'customer'; // default to customer
  get emailControl() {
    return this.resetPasswordForm.get('email');
  }

    // lifecycle hook
  ngOnInit() {
    // Get portal type from route data
    this.portalType = this.route.snapshot.data['portalType'] || 'customer';
    
    this.resetPasswordForm = this.fb.group({
      email: new FormControl('', [Validators.email, Validators.required]),
        captcha: ['', Validators.required] 
    })
  }

  /**
   * Gets the appropriate portal translation key
   * @returns translation key for portal type
   */
  getPortalTranslationKey(): string {
    return this.portalType === 'partner' ? 'navbar.partnerPortal' : 'signIn.customerPortal';
  }

  // Resets password for the customer/partner if captcha is resolved and form is valid.
  resetPassword() {
    if(this.resetPasswordForm.valid && this.captchaResolved){
    console.log(`==> ${this.portalType} reset password controller`);
    this.isLoading = true;
    const payload = {
      emailaddress: sanitizeInput(this.resetPasswordForm.get('email')?.value),
      useralias: this.userAliasDetails ? sanitizeInput(this.userAliasDetails.useralias) : '',
      // useralias: sanitizeInput(this.resetPasswordForm.get('email')?.value)
    };
    this.authService.resetPassword(payload).subscribe({
      next: (response: IResetPasswordResponse) => {
        //this.submitted = true;
        const useraliaslist =
          response['MT_PasswordReset_Resp']['useraliaslist'];
        if (useraliaslist.useralias?.length > 1) {
          this.isDisabled = true;
          this.useraliaslist = this.getFormattedUserData(
            useraliaslist.useralias
          );
          this.submitted = false;
        } else {
          this.submitted = true;
          // const status = response['MT_PasswordReset_Resp']['statuses']['status'] as Status;
          // if (status['statustext']['_text']) {
          //   this.toastService.openSnackbar({
          //     message: status['statustext']['_text'],
          //   });
          // } else {
          //   throw Error('Request Failed');
          // }
          // this.dialogService.closeAllDialog();
          // this.router.navigate(['/customer-portal']);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        let errorDetail = this.translate.instant('resetPassword.unexpected_error');
        console.log(err);
        if(err.error.includes('There is a problem with the resource you are looking for, and it cannot be displayed.')) {
          errorDetail = this.translate.instant('resetPassword.we_cannot_find_a_match');
          this.toastService.openSnackbar({ message: errorDetail });
        }
        else if (err.error === 'we_cannot_find_a_match') {
          errorDetail = this.translate.instant('resetPassword.we_cannot_find_a_match');
          this.toastService.openSnackbar({ message: errorDetail });
        } else if (err.error === 'unexpected_error') {
          errorDetail = this.translate.instant('resetPassword.unexpected_error');
          this.toastService.openSnackbar({ message: errorDetail });
        } else {
          this.toastService.openSnackbar({ message: errorDetail });
          throw err;
        }
      },
      complete: () => {
        this.isLoading = false;
      },
    });
   }
}

  // Handles when captcha validation is succeeds.
 handleSuccess(captchaResponse: string) {
  console.log(captchaResponse,'captcha-response')
    this.captchaResolved = true;
    this.resetPasswordForm.patchValue({ captcha: captchaResponse });
  }

  backToLogin() {
    if (this.portalType === 'partner') {
      // Redirect to external partner portal URL
      window.location.href = environment.portalUrl;
    } else {
      // Default customer portal behavior
      this.dialogService.openSignInDialog();
    }
  }

  /**
   * get selected userid row data.
   * @param row row
   */
  public onSelectedRow(row: FormattedUseraliasData) {
    this.isDisabled = false;
    this.userAliasDetails = row;
  }

  /**
   * format useralias list data
   * @param useraliaslist useraliaslist
   * @returns formatted useraliaslist
   */
  private getFormattedUserData(
    useraliaslist: Useralias[]
  ): FormattedUseraliasData[] {
    return useraliaslist.map((useralias) => ({
      //recordnumber: this.extractText(useralias.recordnumber),
      selection:false,
      useralias: this.extractText(useralias.useralias),
      firstname: this.extractText(useralias.firstname),
      lastname: this.extractText(useralias.lastname),
      companyname: this.extractText(useralias.companyname),
      city: this.extractText(useralias.city)
    }));
  }

  /**
   * check type of field data.
   * @param field field
   * @returns textfield
   */
  private isTextField(field: unknown): field is TextField {
    return (
      typeof field === 'object' &&
      field !== null &&
      '_text' in field &&
      typeof (field as Record<string, unknown>)['_text'] === 'string'
    );
  }

  /**
   * format the text value.
   * @param field field
   * @returns formatted text
   */
  private extractText(field: unknown): string {
    return this.isTextField(field) ? (field as TextField)['_text'] : '';
  }
}
